// ===========================================
// Calendar Legend Component
// ===========================================
// Displays color legend for calendar events based on background and border color settings

import { Box, Chip, Stack, Typography, Divider } from '@mui/material';
import { ColorSource, BorderColorSource, SpecialEventColors } from './CalendarDisplaySettingsModal';
import { LessonType, Instrument, ProgramLevel } from '../../api/admin.api';
import { Teacher } from '../../api/users.api';
import { getEventTypeColor } from '../../api/hybridBooking.api';
import { getContrastTextColor, defaultLessonTypeColors } from '../../styles/brandColors';

/** What the legend reads: the admin rows and the `/calendar/*` picker rows both fit (#272). */
export type LegendInstrument = Pick<Instrument, 'name' | 'color' | 'isActive'>;
export type LegendProgramLevel = Pick<ProgramLevel, 'name' | 'color' | 'isActive'>;

// ===========================================
// TYPES
// ===========================================

export interface CalendarLegendProps {
  backgroundColorSource: ColorSource;
  borderColorSource: BorderColorSource;
  lessonTypes: LessonType[];
  teachers: Teacher[];
  instruments: LegendInstrument[];
  programLevels: LegendProgramLevel[];
  showSpecialEvents?: boolean;
  specialEventColors?: SpecialEventColors;
}

// Special event types that are always shown
const SPECIAL_EVENTS = [
  { label: 'Hybrid (Group Week)', type: 'HYBRID_GROUP' },
  { label: 'Hybrid (Individual)', type: 'HYBRID_INDIVIDUAL' },
  { label: 'Booking Placeholder', type: 'HYBRID_PLACEHOLDER' },
  { label: 'Meet & Greet', type: 'MEET_AND_GREET' },
  { label: 'Extra Lesson', type: 'EXTRA_LESSON' },
  { label: 'Make-up', type: 'MAKEUP' },
];

// ===========================================
// COMPONENT
// ===========================================

export default function CalendarLegend({
  backgroundColorSource,
  borderColorSource,
  lessonTypes,
  teachers,
  instruments,
  programLevels,
  showSpecialEvents = true,
  specialEventColors,
}: CalendarLegendProps) {
  // Get items to display based on color source
  const getColorItems = (colorSource: ColorSource): { name: string; color: string }[] => {
    switch (colorSource) {
      case 'lessonType':
        return lessonTypes
          .filter((lt) => lt.isActive)
          .map((lt) => ({
            name: lt.name,
            color: lt.color || defaultLessonTypeColors[lt.type] || getEventTypeColor(lt.type),
          }));
      case 'teacher':
        return teachers
          .filter((t) => t.isActive && t.color)
          .map((t) => ({
            name: `${t.User.firstName} ${t.User.lastName}`,
            color: t.color!,
          }));
      case 'instrument':
        return instruments
          .filter((i) => i.isActive && i.color && i.color.trim())
          .map((i) => ({
            name: i.name,
            color: i.color!,
          }));
      case 'programLevel':
        return programLevels
          .filter((pl) => pl.isActive && pl.color)
          .map((pl) => ({
            name: pl.name,
            color: pl.color!,
          }));
      default:
        return [];
    }
  };

  // Get source label for display
  const getSourceLabel = (colorSource: ColorSource): string => {
    switch (colorSource) {
      case 'lessonType':
        return 'Lesson Types';
      case 'teacher':
        return 'Teachers';
      case 'instrument':
        return 'Instruments';
      case 'programLevel':
        return 'Program Levels';
      default:
        return '';
    }
  };

  const backgroundItems = getColorItems(backgroundColorSource);
  const borderItems = borderColorSource !== 'none' ? getColorItems(borderColorSource) : [];
  const showBorderSection = borderColorSource !== 'none';

  // Check if background and border use the same source (avoid duplicate display)
  const sameSource = backgroundColorSource === borderColorSource;

  return (
    <Box>
      {/* Background Color Legend */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Background Color ({getSourceLabel(backgroundColorSource)})
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {backgroundItems.length > 0 ? (
          backgroundItems.map((item) => (
            <Chip
              key={item.name}
              label={item.name}
              size="small"
              sx={{
                backgroundColor: item.color,
                color: getContrastTextColor(item.color),
                fontSize: '11px',
              }}
            />
          ))
        ) : (
          <Typography variant="caption" color="text.secondary">
            No {getSourceLabel(backgroundColorSource).toLowerCase()} with colors configured
          </Typography>
        )}
      </Stack>

      {/* Border Color Legend - only show if different from background */}
      {showBorderSection && !sameSource && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Border Color ({getSourceLabel(borderColorSource as ColorSource)})
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {borderItems.length > 0 ? (
              borderItems.map((item) => (
                <Chip
                  key={item.name}
                  label={item.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: item.color,
                    borderWidth: 2,
                    color: item.color,
                    fontSize: '11px',
                    '& .MuiChip-label': {
                      fontWeight: 500,
                    },
                  }}
                />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                No {getSourceLabel(borderColorSource as ColorSource).toLowerCase()} with colors configured
              </Typography>
            )}
          </Stack>
        </>
      )}

      {/* Note if same source is used for both */}
      {showBorderSection && sameSource && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Border color uses the same source as background ({getSourceLabel(backgroundColorSource)})
          </Typography>
        </>
      )}

      {/* Note if no border */}
      {!showBorderSection && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Border color: None
          </Typography>
        </>
      )}

      {/* Special event types */}
      {showSpecialEvents && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Special Events
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {SPECIAL_EVENTS.map((event) => {
              const color = getEventTypeColor(event.type, specialEventColors);
              return (
                <Chip
                  key={event.type}
                  label={event.label}
                  size="small"
                  sx={{
                    backgroundColor: color,
                    color: getContrastTextColor(color),
                    fontSize: '11px',
                  }}
                />
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );
}
